import { UHttp } from "../../utils/UHttp";

const apiUrlServer = process.env.REACT_APP_URL_SERVER;

/**
 * Interface pour la réponse de l'API
 */
interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
}

/**
 * Interface pour les paramètres de création d'une session de paiement
 * Les données de réservation sont maintenant envoyées dans bookingData
 * La réservation sera créée après confirmation du paiement via webhook
 */
export interface CreateCheckoutSessionParams {
  bookingData: {
    timeSlotId: string | null;
    // Legacy single service format (deprecated)
    serviceId?: string;
    optionIds?: string[];
    // Multi-services format
    services?: Array<{
      serviceId: string;
      optionIds?: string[];
      price: number;
      duration: number;
    }>;
    clientId?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    collaboratorCompagnieId: string;
    startTime: string;
    endTime: string;
    note: string | null;
    totalPrice: number;
  };
  paymentType?: 'full' | 'deposit';
  depositPercentage?: number;
  promoCodeId?: string;
  discountAmount?: number;
}

/**
 * Interface pour la réponse de création de session
 */
export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

/**
 * Interface pour les détails d'un paiement
 */
export interface PaymentDetails {
  id: string;
  bookingId: string;
  companyId: string;
  clientId: string;
  stripePaymentIntentId: string;
  amount: number;
  amountPaid: number;
  depositPercentage: number | null;
  discountAmount: number;
  finalAmount: number;
  stripeFee: number;
  totalChargedToClient: number;
  promoCodeId: string | null;
  status: 'pending' | 'succeeded' | 'failed' | 'canceled' | 'refunded';
  paymentType: 'full' | 'deposit';
  currency: string;
  metadata: any;
  createdAt: string;
  updatedAt: string;
}

/**
 * Interface pour le statut d'une session de paiement
 */
export interface SessionStatus {
  status: string;
  paymentStatus: string;
  amountTotal: number;
}

/**
 * Modèle pour gérer les paiements côté client
 */
class ClientPaymentModel {
  private static instance: ClientPaymentModel;

  private constructor() {}

  /**
   * Récupère l'instance unique du modèle (singleton)
   */
  public static getInstance(): ClientPaymentModel {
    if (!ClientPaymentModel.instance) {
      ClientPaymentModel.instance = new ClientPaymentModel();
    }
    return ClientPaymentModel.instance;
  }

  /**
   * Crée une session de paiement Stripe Checkout
   */
  async createCheckoutSession(
    params: CreateCheckoutSessionParams
  ): Promise<ApiResponse<CheckoutSessionResponse>> {
    try {
      const response = await UHttp.post(
        `${apiUrlServer}/public-api/payment/create-checkout-session`,
        params,
        UHttp.publicApiHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la création de la session de paiement',
        };
      }

      const data = await response.json();

      if (data.success && data.data) {
        return {
          success: true,
          message: 'Session de paiement créée avec succès',
          data: data.data,
        };
      } else {
        return {
          success: false,
          message: data.message || 'Erreur lors de la création de la session de paiement',
        };
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      return {
        success: false,
        message: error.message || 'Erreur réseau',
      };
    }
  }

  /**
   * Récupère les détails d'un paiement
   */
  async getPaymentDetails(paymentId: string): Promise<ApiResponse<PaymentDetails>> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/public-api/payment/${paymentId}`,
        UHttp.publicApiHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la récupération du paiement',
        };
      }

      const data = await response.json();

      if (data.success && data.data) {
        return {
          success: true,
          message: 'Paiement récupéré avec succès',
          data: data.data,
        };
      } else {
        return {
          success: false,
          message: data.message || 'Erreur lors de la récupération du paiement',
        };
      }
    } catch (error: any) {
      console.error('Error fetching payment details:', error);
      return {
        success: false,
        message: error.message || 'Erreur réseau',
      };
    }
  }

  /**
   * Vérifie le statut d'une session de paiement
   * @param sessionId - ID de la session Stripe
   * @param stripeAccountId - ID du compte connecté (optionnel, requis pour Direct Charges)
   */
  async checkSessionStatus(sessionId: string, stripeAccountId?: string): Promise<ApiResponse<SessionStatus>> {
    try {
      const url = stripeAccountId
        ? `${apiUrlServer}/public-api/payment/session/${sessionId}/status?stripeAccountId=${stripeAccountId}`
        : `${apiUrlServer}/public-api/payment/session/${sessionId}/status`;

      const response = await UHttp.get(
        url,
        UHttp.publicApiHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la vérification du statut',
        };
      }

      const data = await response.json();

      if (data.success && data.data) {
        return {
          success: true,
          message: 'Statut récupéré avec succès',
          data: data.data,
        };
      } else {
        return {
          success: false,
          message: data.message || 'Erreur lors de la vérification du statut',
        };
      }
    } catch (error: any) {
      console.error('Error checking session status:', error);
      return {
        success: false,
        message: error.message || 'Erreur réseau',
      };
    }
  }
}

export default ClientPaymentModel;
